import React, { useEffect } from 'react';
import { usePrivacySettings } from '../../../insfrastructure/hooks/usePrivacySettings';
import { useResponsive } from '../../../insfrastructure/hooks/useResponsive';

interface PrivacySettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PrivacySettings: React.FC<PrivacySettingsProps> = ({ isOpen, onClose }) => {
  const {
    settings,
    isLoading,
    updateSetting,
    saveSettings,
    resetSettings,
    enabledCount,
    totalCount
  } = usePrivacySettings();
  const { isMobile } = useResponsive();

  useEffect(() => {
    if (isOpen) {
      // Recarregar configurações quando o modal abrir
      // O hook já gerencia isso automaticamente
    }
  }, [isOpen]);

  const handleToggle = (setting: keyof typeof settings) => {
    updateSetting(setting, !settings[setting]);
  };

  const handleSave = async () => {
    const success = await saveSettings(settings);
    if (success) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 10000,
      display: 'flex',
      alignItems: isMobile ? 'flex-end' : 'center',
      justifyContent: 'center',
      padding: isMobile ? '0' : '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: isMobile ? '12px 12px 0 0' : '12px',
        padding: isMobile ? '20px' : '32px',
        maxWidth: isMobile ? '100%' : '600px',
        width: '100%',
        maxHeight: isMobile ? '85vh' : '90vh',
        overflow: 'auto',
        boxShadow: isMobile ? '0 -4px 20px rgba(0, 0, 0, 0.15)' : '0 20px 40px rgba(0, 0, 0, 0.15)',
        marginTop: isMobile ? 'auto' : '0'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '1px solid #e9ecef'
        }}>
          <div>
            <h2 style={{
              margin: 0,
              color: '#105099',
              fontSize: '24px',
              fontWeight: '600'
            }}>
              🛡️ Configurações de Privacidade
            </h2>
            <p style={{
              margin: '4px 0 0 0',
              fontSize: '14px',
              color: '#666'
            }}>
              {enabledCount} de {totalCount} configurações habilitadas
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '4px'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ marginBottom: '24px' }}>
          <p style={{
            color: '#666',
            marginBottom: '16px',
            lineHeight: '1.5'
          }}>
            Configure suas preferências de privacidade. Você pode habilitar ou desabilitar 
            diferentes tipos de monitoramento conforme sua preferência.
          </p>

          {/* Controles globais */}
          <div style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '24px',
            padding: '12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px',
            border: '1px solid #e9ecef'
          }}>
            <button
              onClick={() => {
                const allEnabled = {
                  errorTracking: true,
                  performanceMonitoring: true,
                  sessionReplay: true,
                  analytics: true
                };
                Object.keys(allEnabled).forEach(key => {
                  updateSetting(key as keyof typeof settings, true);
                });
              }}
              disabled={isLoading}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                fontWeight: '500'
              }}
            >
              Habilitar Todas
            </button>
            
            <button
              onClick={() => {
                const allDisabled = {
                  errorTracking: false,
                  performanceMonitoring: false,
                  sessionReplay: false,
                  analytics: false
                };
                Object.keys(allDisabled).forEach(key => {
                  updateSetting(key as keyof typeof settings, false);
                });
              }}
              disabled={isLoading}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                fontWeight: '500'
              }}
            >
              Desabilitar Todas
            </button>
          </div>

          {/* Settings */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            {/* Error Tracking */}
            <div style={{
              padding: '16px',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              backgroundColor: '#f8f9fa'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <h3 style={{
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  Rastreamento de Erros
                </h3>
                <label style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: '50px',
                  height: '24px'
                }}>
                  <input
                    type="checkbox"
                    checked={settings.errorTracking}
                    onChange={() => handleToggle('errorTracking')}
                    style={{ display: 'none' }}
                  />
                  <span style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: settings.errorTracking ? '#105099' : '#ccc',
                    borderRadius: '24px',
                    transition: '0.3s'
                  }}>
                    <span style={{
                      position: 'absolute',
                      content: '""',
                      height: '18px',
                      width: '18px',
                      left: settings.errorTracking ? '28px' : '3px',
                      bottom: '3px',
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      transition: '0.3s'
                    }} />
                  </span>
                </label>
              </div>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#666',
                lineHeight: '1.4'
              }}>
                Coleta informações sobre erros técnicos para melhorar a estabilidade da aplicação.
                <strong> Não inclui dados pessoais.</strong>
              </p>
            </div>

            {/* Performance Monitoring */}
            <div style={{
              padding: '16px',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              backgroundColor: '#f8f9fa'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <h3 style={{
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  Monitoramento de Performance
                </h3>
                <label style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: '50px',
                  height: '24px'
                }}>
                  <input
                    type="checkbox"
                    checked={settings.performanceMonitoring}
                    onChange={() => handleToggle('performanceMonitoring')}
                    style={{ display: 'none' }}
                  />
                  <span style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: settings.performanceMonitoring ? '#105099' : '#ccc',
                    borderRadius: '24px',
                    transition: '0.3s'
                  }}>
                    <span style={{
                      position: 'absolute',
                      content: '""',
                      height: '18px',
                      width: '18px',
                      left: settings.performanceMonitoring ? '28px' : '3px',
                      bottom: '3px',
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      transition: '0.3s'
                    }} />
                  </span>
                </label>
              </div>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#666',
                lineHeight: '1.4'
              }}>
                Mede velocidade de carregamento e performance da aplicação.
                <strong> Dados anônimos apenas.</strong>
              </p>
            </div>

            {/* Session Replay */}
            <div style={{
              padding: '16px',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              backgroundColor: '#f8f9fa'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <h3 style={{
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  Gravação de Sessão
                </h3>
                <label style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: '50px',
                  height: '24px'
                }}>
                  <input
                    type="checkbox"
                    checked={settings.sessionReplay}
                    onChange={() => handleToggle('sessionReplay')}
                    style={{ display: 'none' }}
                  />
                  <span style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: settings.sessionReplay ? '#105099' : '#ccc',
                    borderRadius: '24px',
                    transition: '0.3s'
                  }}>
                    <span style={{
                      position: 'absolute',
                      content: '""',
                      height: '18px',
                      width: '18px',
                      left: settings.sessionReplay ? '28px' : '3px',
                      bottom: '3px',
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      transition: '0.3s'
                    }} />
                  </span>
                </label>
              </div>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#666',
                lineHeight: '1.4'
              }}>
                Grava interações para reproduzir problemas técnicos.
                <strong> Todo texto e mídia são mascarados.</strong>
              </p>
            </div>

            {/* Analytics */}
            <div style={{
              padding: '16px',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              backgroundColor: '#f8f9fa'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <h3 style={{
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  Analytics Básico
                </h3>
                <label style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: '50px',
                  height: '24px'
                }}>
                  <input
                    type="checkbox"
                    checked={settings.analytics}
                    onChange={() => handleToggle('analytics')}
                    style={{ display: 'none' }}
                  />
                  <span style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: settings.analytics ? '#105099' : '#ccc',
                    borderRadius: '24px',
                    transition: '0.3s'
                  }}>
                    <span style={{
                      position: 'absolute',
                      content: '""',
                      height: '18px',
                      width: '18px',
                      left: settings.analytics ? '28px' : '3px',
                      bottom: '3px',
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      transition: '0.3s'
                    }} />
                  </span>
                </label>
              </div>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#666',
                lineHeight: '1.4'
              }}>
                Coleta métricas básicas de uso para melhorar a experiência.
                <strong> Sem identificação pessoal.</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '16px',
          borderTop: '1px solid #e9ecef',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={resetSettings}
            disabled={isLoading}
            style={{
              backgroundColor: 'transparent',
              color: '#666',
              border: '1px solid #dee2e6',
              padding: '12px 24px',
              borderRadius: '6px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Resetar
          </button>
          
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              disabled={isLoading}
              style={{
                backgroundColor: 'transparent',
                color: '#105099',
                border: '1px solid #105099',
                padding: '12px 24px',
                borderRadius: '6px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Cancelar
            </button>
            
            <button
              onClick={handleSave}
              disabled={isLoading}
              style={{
                backgroundColor: '#105099',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '6px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: isLoading ? 0.7 : 1
              }}
            >
              {isLoading ? 'Salvando...' : 'Salvar Configurações'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacySettings;
